import { create } from "zustand";
import { Screen, DetailsStep } from "@/types/Navigation";
import {
  scrollToTop,
  enableBeforeUnload,
  disableBeforeUnload,
} from "@/utils/dom";
import { dispatchEvent } from "@/utils/event-dispacher";
import { EventName } from "@/types/Events";
import type { SubmissionSuccessResponse } from "@/types/Payment";

export type NavigationState = {
  screen: Screen;
  visibleDetailsSteps: DetailsStep[];
  currentDetailsStep: DetailsStep | null;
  submissionResponse: SubmissionSuccessResponse | null;
  setScreen: (screen: Screen) => void;
  setCurrentDetailsStep: (step: DetailsStep | null) => void;
  removeVisibleDetailsSteps: (steps: DetailsStep[]) => void;
  completeSubmission: (response: SubmissionSuccessResponse) => void;
  reset: () => void;
};

export const useNavigationStore = create<NavigationState>((set, get) => ({
  screen: Screen.Amount,
  visibleDetailsSteps: [],
  currentDetailsStep: null,
  submissionResponse: null,
  setScreen: (screen) => {
    scrollToTop();

    if (screen === Screen.Details) {
      enableBeforeUnload();
    }

    if (screen === Screen.Success) {
      disableBeforeUnload();
    }

    set({ screen });
  },
  setCurrentDetailsStep: (step) => {
    if (step === null) {
      set({ currentDetailsStep: null });
      return;
    }

    dispatchEvent(EventName.STEP_CHANGE, {
      step_name: step,
    });

    const { visibleDetailsSteps } = get();
    if (!visibleDetailsSteps.includes(step)) {
      set((state) => ({
        visibleDetailsSteps: [...state.visibleDetailsSteps, step],
      }));
    }
    set({ currentDetailsStep: step });
  },
  removeVisibleDetailsSteps: (steps) =>
    set((state) => ({
      visibleDetailsSteps: state.visibleDetailsSteps.filter(
        (step) => !steps.includes(step),
      ),
    })),
  completeSubmission: (response) => {
    scrollToTop();
    disableBeforeUnload();
    set({ screen: Screen.Success, submissionResponse: response });
  },
  reset: () =>
    set({
      screen: Screen.Amount,
      visibleDetailsSteps: [],
      currentDetailsStep: null,
      submissionResponse: null,
    }),
}));
